import { TransitionSound } from "@repo/api/transition";
import fastAirSwoosh from "../../assets/sounds/transitions/fast-air-swoosh.mp3";
import softCinematicWhoosh from "../../assets/sounds/transitions/soft-cinematic-whoosh.mp3";
import shortSoftWhoosh from "../../assets/sounds/transitions/short-soft-whoosh.mp3";
import swoosh1 from "../../assets/sounds/transitions/swoosh-1.mp3";
import epicCinematicBoom from "../../assets/sounds/transitions/epic-cinematic-boom.mp3";
import subtleFuturisticBlip from "../../assets/sounds/transitions/subtle-futuristic-blip.mp3";
import shortGlitch from "../../assets/sounds/transitions/short-glitch.mp3";
import digitalGlitchSwipe from "../../assets/sounds/transitions/digital-glitch-swipe.mp3";
import cinematicImpact from "../../assets/sounds/transitions/cinematic-impact.mp3";
import softWhoosh from "../../assets/sounds/transitions/soft-whoosh.mp3";
import shortSoftTransition from "../../assets/sounds/transitions/short-soft-transition.mp3";
import cleanUiWhoosh from "../../assets/sounds/transitions/clean-ui-whoosh.mp3";
import cleanTechWhoosh from "../../assets/sounds/transitions/clean-tech-whoosh.mp3";
import cleanTicking from "../../assets/sounds/transitions/clean-ticking.mp3";
import elegantRiser from "../../assets/sounds/transitions/elegant-riser.mp3";
import softRiserWhoosh from "../../assets/sounds/transitions/soft-riser-whoosh.mp3";
import epicCinematicBoom2 from "../../assets/sounds/transitions/epic-cinematic-boom-2.mp3";
import cinematicBassHit from "../../assets/sounds/transitions/cinematic-bass-hit.mp3";
import magicalReveal from "../../assets/sounds/transitions/magical-reveal.mp3";
import arcaneChime from "../../assets/sounds/transitions/arcane-chime.mp3";
import mysticalAmbience from "../../assets/sounds/transitions/mystical-ambience.mp3";
import cameraFlash from "../../assets/sounds/transitions/camera-flash.mp3";
import cameraShutter from "../../assets/sounds/transitions/camera-shutter.mp3";
import vintageFilmCamera from "../../assets/sounds/transitions/vintage-film-camera.mp3";
import softTicking from "../../assets/sounds/transitions/soft-ticking.mp3";

// Bundled with the code (not public/) so Remotion Lambda and studio both resolve them.
// Kept out of the builders: the backend imports those in Node, where .mp3 imports fail.
type TransitionSoundFile = {
  src: string;
  loudnessLufs: number;
  gainDb?: number;
} & (
  | {
      peakMs: number;
    }
  | {
      duringTransitionFromMs: number;
    }
);

export const TRANSITION_SOUNDS: Record<TransitionSound, TransitionSoundFile> = {
  FAST_AIR_SWOOSH: { src: fastAirSwoosh, loudnessLufs: -13.8, peakMs: 530 },
  SOFT_CINEMATIC_WHOOSH: {
    src: softCinematicWhoosh,
    loudnessLufs: -15.1,
    gainDb: 1.5,
    peakMs: 560,
  },
  SHORT_SOFT_WHOOSH: {
    src: shortSoftWhoosh,
    loudnessLufs: -17.6,
    gainDb: 3,
    peakMs: 550,
  },
  SWOOSH_1: { src: swoosh1, loudnessLufs: -20.4, peakMs: 420 },
  EPIC_CINEMATIC_BOOM: {
    src: epicCinematicBoom,
    loudnessLufs: -11.4,
    gainDb: 3,
    peakMs: 380,
  },
  SUBTLE_FUTURISTIC_BLIP: {
    src: subtleFuturisticBlip,
    loudnessLufs: -18.1,
    gainDb: 3,
    peakMs: 200,
  },
  SHORT_GLITCH: { src: shortGlitch, loudnessLufs: -12.7, peakMs: 220 },
  DIGITAL_GLITCH_SWIPE: {
    src: digitalGlitchSwipe,
    loudnessLufs: -21.3,
    peakMs: 110,
  },
  CINEMATIC_IMPACT: {
    src: cinematicImpact,
    loudnessLufs: -7.8,
    peakMs: 485,
  },
  SOFT_WHOOSH: { src: softWhoosh, loudnessLufs: -18.8, peakMs: 395 },
  SHORT_SOFT_TRANSITION: {
    src: shortSoftTransition,
    loudnessLufs: -18.8,
    peakMs: 305,
  },
  CLEAN_UI_WHOOSH: {
    src: cleanUiWhoosh,
    loudnessLufs: -12.0,
    gainDb: 3,
    peakMs: 305,
  },
  CLEAN_TECH_WHOOSH: {
    src: cleanTechWhoosh,
    loudnessLufs: -10.5,
    gainDb: 4.5,
    peakMs: 255,
  },
  CLEAN_TICKING: {
    src: cleanTicking,
    loudnessLufs: -21.6,
    gainDb: -3,
    duringTransitionFromMs: 150,
  },
  ELEGANT_RISER: { src: elegantRiser, loudnessLufs: -11.4, peakMs: 585 },
  SOFT_RISER_WHOOSH: { src: softRiserWhoosh, loudnessLufs: -12.6, peakMs: 445 },
  EPIC_CINEMATIC_BOOM_2: {
    src: epicCinematicBoom2,
    loudnessLufs: -10.1,
    peakMs: 1695,
  },
  CINEMATIC_BASS_HIT: {
    src: cinematicBassHit,
    loudnessLufs: -13.0,
    peakMs: 1745,
  },
  MAGICAL_REVEAL: { src: magicalReveal, loudnessLufs: -20.7, peakMs: 275 },
  ARCANE_CHIME: { src: arcaneChime, loudnessLufs: -24.6, peakMs: 135 },
  MYSTICAL_AMBIENCE: { src: mysticalAmbience, loudnessLufs: -30.7, peakMs: 25 },
  CAMERA_FLASH: { src: cameraFlash, loudnessLufs: -16.0, peakMs: 185 },
  CAMERA_SHUTTER: { src: cameraShutter, loudnessLufs: -17.4, peakMs: 55 },
  VINTAGE_FILM_CAMERA: {
    src: vintageFilmCamera,
    loudnessLufs: -19.0,
    peakMs: 335,
  },
  SOFT_TICKING: {
    src: softTicking,
    loudnessLufs: -26.5,
    gainDb: -3,
    duringTransitionFromMs: 120,
  },
};

const TARGET_LOUDNESS_LUFS = -27;

export function getTransitionSoundVolume(sound: TransitionSound) {
  const { loudnessLufs, gainDb = 0 } = TRANSITION_SOUNDS[sound];
  const totalGainDb = TARGET_LOUDNESS_LUFS - loudnessLufs + gainDb;
  return Math.min(1, 10 ** (totalGainDb / 20));
}
